// Platform (superadmin only): tallerza.cl traffic from the daily aggregates
// the `evento` Function keeps in `metricas/{YYYY-MM-DD}`. No personal data:
// only counters per page, event, referrer, campaign and device. The range
// lives in the URL (?dias=7|30|90).
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChartColumn } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useMetricas } from '@/datos/consultas';
import { exigir } from '@/lib/guardias';
import { permisos } from '@/lib/sesion';
import { fechaDeIso } from '@/lib/tiempo';
import { GraficoVistas } from '@/plataforma/GraficoVistas';
import {
  busquedaMetricas,
  diasDelRango,
  entero,
  etiquetaCampana,
  etiquetaDispositivo,
  etiquetaPagina,
  etiquetaReferente,
  porcentaje,
  RANGO_POR_DEFECTO,
  RANGOS,
  resumir,
  type Fila,
  type Rango,
  type Resumen,
} from '@/plataforma/metricas';
import { ErrorCarga, Esqueleto, Segmentado, Tarjeta, TituloTarjeta, Vacio } from '@/ui/basicos';
import { cn } from '@/ui/cn';
import { Contenedor } from '@/ui/pagina';

export const Route = createFileRoute('/_app/plataforma/metricas')({
  validateSearch: busquedaMetricas,
  beforeLoad: exigir(permisos.plataforma),
  component: Pagina,
});

function Pagina() {
  const { dias: elegido } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const dias: Rango = elegido ?? RANGO_POR_DEFECTO;
  // The range is computed once per choice (a page left open overnight keeps
  // its range until the selector changes or the page reloads).
  const lista = useMemo(() => diasDelRango(dias), [dias]);
  const desde = lista[0] ?? '';
  const hasta = lista[lista.length - 1] ?? '';
  const q = useMetricas(desde, hasta);
  const [tablaAbierta, setTablaAbierta] = useState(false);
  const r = useMemo(() => (q.data ? resumir(q.data, lista) : null), [q.data, lista]);

  return (
    <Contenedor>
      <div className="flex flex-wrap items-end gap-3">
        {/* basis: on phones the period control wraps below instead of squeezing the text. */}
        <div className="min-w-0 flex-1 basis-[260px]">
          <h1 className="titulo text-[24px] leading-tight">Métricas</h1>
          <p className="text-[15px] text-text-2">
            Visitas y conversiones de tallerza.cl · del {fechaDeIso(desde)} al {fechaDeIso(hasta)}
          </p>
        </div>
        <Segmentado<`${Rango}`>
          etiqueta="Período"
          valor={`${dias}`}
          onCambio={(v) => {
            const n = Number(v) as Rango;
            void navegar({
              search: { dias: n === RANGO_POR_DEFECTO ? undefined : n },
              replace: true,
            });
          }}
          opciones={RANGOS.map((n) => ({ valor: `${n}` as const, texto: `${n} días` }))}
        />
      </div>

      {q.isError ? (
        <ErrorCarga error={q.error} reintentar={() => void q.refetch()} />
      ) : !r ? (
        <Cargando />
      ) : r.diasConDatos === 0 ? (
        <Tarjeta>
          <Vacio
            icono={<ChartColumn />}
            titulo={`Sin visitas registradas en los últimos ${dias} días`}
          >
            Las visitas y los clics del sitio se cuentan por día. Si el sitio está publicado y aún
            no aparece nada, prueba con un período más largo.
          </Vacio>
        </Tarjeta>
      ) : (
        <Panel r={r} tablaAbierta={tablaAbierta} onTabla={setTablaAbierta} />
      )}
      <p className="text-[13px] text-text-2">
        Solo se guardan conteos por día (página, evento, origen, campaña y tipo de dispositivo):
        nada que identifique a una persona. Las visitas de bots no se cuentan.
      </p>
    </Contenedor>
  );
}

function Cargando() {
  return (
    <div aria-busy="true" aria-label="Cargando métricas" className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => (
          <Esqueleto key={i} className="h-[92px] rounded-tarjeta" />
        ))}
      </div>
      <Esqueleto className="h-[280px] rounded-tarjeta" />
      <div className="grid gap-5 lg:grid-cols-3">
        <Esqueleto className="h-48 rounded-tarjeta" />
        <Esqueleto className="h-48 rounded-tarjeta" />
        <Esqueleto className="h-48 rounded-tarjeta" />
      </div>
    </div>
  );
}

function Panel({
  r,
  tablaAbierta,
  onTabla,
}: {
  r: Resumen;
  tablaAbierta: boolean;
  onTabla: (abierta: boolean) => void;
}) {
  const totalDispositivos = r.dispositivos.reduce((s, f) => s + f.valor, 0);
  return (
    <>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Kpi etiqueta="Vistas" valor={entero(r.vistas)} />
        <Kpi etiqueta="Leads" valor={entero(r.leads)} nota="Demos y lista de espera" />
        <Kpi etiqueta="Conversión" valor={porcentaje(r.conversion)} nota="Leads / vistas" />
        <Kpi etiqueta="Clics a WhatsApp" valor={entero(r.whatsapp)} />
        <Kpi etiqueta="Aperturas de demo" valor={entero(r.demoAbierta)} nota="Formulario abierto" />
        <Kpi
          etiqueta="Clics en Ingresar"
          valor={entero(r.ingresar)}
          nota="Clientes al backoffice"
        />
      </dl>

      <Tarjeta className="flex flex-col gap-4 p-5">
        <TituloTarjeta>Vistas por día</TituloTarjeta>
        <GraficoVistas puntos={r.porDia} tablaAbierta={tablaAbierta} onTabla={onTabla} />
      </Tarjeta>

      <div className="grid gap-5 lg:grid-cols-3">
        <Tarjeta className="flex flex-col gap-4 p-5">
          <TituloTarjeta>Páginas más vistas</TituloTarjeta>
          <Ranking
            filas={r.paginas}
            etiqueta={etiquetaPagina}
            unidad={['vista', 'vistas']}
            mono
            vacio="Sin vistas en el período."
          />
        </Tarjeta>
        <Tarjeta className="flex flex-col gap-4 p-5">
          <TituloTarjeta>Orígenes</TituloTarjeta>
          <Ranking
            filas={r.referentes}
            etiqueta={etiquetaReferente}
            unidad={['visita', 'visitas']}
            vacio="Nadie llegó desde otro sitio: las visitas fueron directas."
          />
        </Tarjeta>
        <Tarjeta className="flex flex-col gap-4 p-5">
          <TituloTarjeta>Dispositivos</TituloTarjeta>
          <Ranking
            filas={r.dispositivos}
            etiqueta={etiquetaDispositivo}
            unidad={['visita', 'visitas']}
            total={totalDispositivos}
            vacio="Sin datos de dispositivos en el período."
          />
        </Tarjeta>
      </div>

      <Tarjeta className="flex flex-col gap-4 p-5">
        <TituloTarjeta>Campañas</TituloTarjeta>
        {r.fuentes.length + r.medios.length + r.campanas.length === 0 ? (
          <p className="text-[14px] text-text-2">
            Ninguna visita llegó con parámetros de campaña (utm_source, utm_medium, utm_campaign).
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            <SubRanking titulo="Fuente" filas={r.fuentes} />
            <SubRanking titulo="Medio" filas={r.medios} />
            <SubRanking titulo="Campaña" filas={r.campanas} />
          </div>
        )}
      </Tarjeta>
    </>
  );
}

/** Stat tile: label, then the value in proportional figures (not tabular). */
function Kpi({ etiqueta, valor, nota }: { etiqueta: string; valor: string; nota?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-tarjeta border border-border bg-surface px-4 py-3.5 shadow-tarjeta">
      <dt className="text-[13px] font-bold text-text-long">{etiqueta}</dt>
      <dd className="titulo text-[28px] leading-none font-bold text-text">{valor}</dd>
      {nota && <dd className="text-[12px] text-text-2">{nota}</dd>}
    </div>
  );
}

function SubRanking({ titulo, filas }: { titulo: string; filas: Fila[] }) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <h3 className="text-[12px] font-bold tracking-[0.06em] text-text-2 uppercase">{titulo}</h3>
      <Ranking
        filas={filas}
        etiqueta={etiquetaCampana}
        unidad={['visita', 'visitas']}
        vacio="Sin datos."
      />
    </div>
  );
}

const MAX_FILAS = 8;

/**
 * Ranked list with a thin proportional bar under each label. The numbers
 * are text (read by screen readers); the bars are decoration.
 */
function Ranking({
  filas,
  etiqueta = (k) => k,
  unidad,
  total,
  mono,
  vacio,
}: {
  filas: Fila[];
  etiqueta?: (clave: string) => string;
  unidad: [string, string];
  /** When given, each row also shows its share of it. */
  total?: number;
  mono?: boolean;
  vacio: ReactNode;
}) {
  if (filas.length === 0) return <p className="text-[14px] text-text-2">{vacio}</p>;
  const visibles = filas.slice(0, MAX_FILAS);
  const resto = filas.length - visibles.length;
  const max = visibles[0]?.valor ?? 1;
  return (
    <div className="flex flex-col gap-2">
      <ol className="flex flex-col gap-2.5">
        {visibles.map((f) => {
          const texto = etiqueta(f.clave);
          return (
            <li key={f.clave} className="flex flex-col gap-1">
              <div className="flex items-baseline gap-3 text-[14px]">
                <span
                  className={cn('min-w-0 flex-1 truncate', mono && 'font-mono text-[13px]')}
                  title={texto}
                >
                  {texto}
                </span>
                <span className="tabular flex-none text-text">
                  {entero(f.valor)}
                  <span className="sr-only"> {f.valor === 1 ? unidad[0] : unidad[1]}</span>
                  {total ? (
                    <span className="ml-1.5 text-[13px] text-text-2">
                      {porcentaje(f.valor / total)}
                    </span>
                  ) : null}
                </span>
              </div>
              <div aria-hidden="true" className="h-1 overflow-hidden rounded-full bg-surface-muted">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${Math.max(2, (f.valor / max) * 100)}%` }}
                />
              </div>
            </li>
          );
        })}
      </ol>
      {resto > 0 && (
        <p className="text-[13px] text-text-2">
          y {resto} más con menos {unidad[1]}
        </p>
      )}
    </div>
  );
}
